import React, { useState } from "react";
import GalleryModal from "./GalleryModal";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../../firebase";
import { galleryItems as staticGallery } from "../../utils/GalleryData";

const GOLD = "#ffc000";
const PRIMARY = "#000c66";
const PAGE_SIZE = 9;

export const GallerySection = () => {
  const [galleryItems, setGalleryItems] = React.useState([]);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [clickedImg, setClickedImg] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(null);

  React.useEffect(() => {
    const getGallery = async () => {
      try {
        const q = query(collection(db, "gallery"), orderBy("createdAt", "desc"));
        const snap = await getDocs(q);
        const items = snap.docs.map((d) => ({ _id: d.id, ...d.data() }));
        setGalleryItems(items.length > 0 ? items : staticGallery);
      } catch (err) {
        setGalleryItems(staticGallery);
      }
    };
    getGallery();
  }, []);

  const handleClick = (item, index) => {
    setCurrentIndex(index);
    setClickedImg(item.imgSrc || item.link);
  };

  const handelRotationRight = () => {
    const total = galleryItems.length;
    const next = currentIndex + 1 >= total ? 0 : currentIndex + 1;
    setCurrentIndex(next);
    setClickedImg(galleryItems[next].imgSrc || galleryItems[next].link);
  };

  const handelRotationLeft = () => {
    const total = galleryItems.length;
    const prev = currentIndex === 0 ? total - 1 : currentIndex - 1;
    setCurrentIndex(prev);
    setClickedImg(galleryItems[prev].imgSrc || galleryItems[prev].link);
  };

  const shown = galleryItems.slice(0, visible);
  const hasMore = visible < galleryItems.length;

  return (
    <section style={{ background: "#f8f7f3", position: "relative" }}>
      <div
        className="mx-auto px-4 md:px-8 lg:px-16 py-12"
        style={{ maxWidth: 1200 }}
      >
        {/* Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 16,
          }}
        >
          {shown.map((item, index) => (
            <div
              key={item._id || index}
              onClick={() => handleClick(item, index)}
              className="group"
              style={{
                position: "relative",
                overflow: "hidden",
                borderRadius: 8,
                cursor: "pointer",
                border: `1px solid ${GOLD}22`,
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                transition: "transform 0.25s ease, box-shadow 0.25s ease",
                aspectRatio: "4/3",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px)";
                e.currentTarget.style.boxShadow = `0 16px 36px rgba(0,12,102,0.14)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.07)";
              }}
            >
              {/* Image */}
              <img
                src={item.imgSrc || item.link}
                alt={item.title || ""}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center",
                  display: "block",
                  transition: "transform 0.5s ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.08)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
              />

              {/* Gold corner brackets — visible on hover */}
              {[
                { top: 8, left: 8, borderTop: `2px solid ${GOLD}`, borderLeft: `2px solid ${GOLD}` },
                { top: 8, right: 8, borderTop: `2px solid ${GOLD}`, borderRight: `2px solid ${GOLD}` },
                { bottom: 8, left: 8, borderBottom: `2px solid ${GOLD}`, borderLeft: `2px solid ${GOLD}` },
                { bottom: 8, right: 8, borderBottom: `2px solid ${GOLD}`, borderRight: `2px solid ${GOLD}` },
              ].map((s, i) => (
                <span
                  key={i}
                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ position: "absolute", width: 16, height: 16, zIndex: 3, ...s }}
                />
              ))}

              {/* Dark overlay + title on hover */}
              <div
                className="opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  position: "absolute",
                  inset: 0,
                  background: `linear-gradient(to top, rgba(0,12,102,0.75) 0%, rgba(0,0,0,0.2) 60%, transparent 100%)`,
                  zIndex: 2,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  padding: "14px 14px 16px",
                }}
              >
                {item.title && (
                  <p
                    className="font-aref font-bold text-white text-sm line-clamp-2 leading-snug"
                  >
                    {item.title}
                  </p>
                )}
                <div className="flex items-center gap-1 mt-1.5">
                  <span className="font-abel text-xs uppercase tracking-widest" style={{ color: GOLD }}>
                    View
                  </span>
                  <svg viewBox="0 0 24 24" width={10} height={10} fill={GOLD}>
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Load More */}
        {hasMore && (
          <div className="flex justify-center mt-10">
            <button
              onClick={() => setVisible((v) => v + PAGE_SIZE)}
              className="flex items-center gap-2 font-abel font-bold uppercase tracking-widest text-sm px-10 py-3.5 rounded-full transition-all duration-200 hover:scale-105"
              style={{
                background: GOLD,
                color: PRIMARY,
                boxShadow: `0 6px 24px ${GOLD}44`,
              }}
            >
              Load More
              <svg viewBox="0 0 24 24" width={14} height={14} fill={PRIMARY}>
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </button>
          </div>
        )}

        {!hasMore && galleryItems.length > PAGE_SIZE && (
          <p className="text-center font-abel text-sm mt-8" style={{ color: "#bbb" }}>
            All {galleryItems.length} photos loaded
          </p>
        )}
      </div>

      {/* Lightbox */}
      {clickedImg && (
        <GalleryModal
          clickedImg={clickedImg}
          handelRotationRight={handelRotationRight}
          setClickedImg={setClickedImg}
          handelRotationLeft={handelRotationLeft}
        />
      )}
    </section>
  );
};
